// src/components/instructor/ops/DayOps.js
//
// Pass 49B §12/§21 — Day operations. Rows are the SELECTED-SEASON operational staff (drivers
// included, not teaching-only). One person can hold several activity types in the same day
// (e.g. Dom 09:00–15:00 Lesson + 16:00–17:00 Driving; Jed driving→concierge→driving). Read-only
// for historical seasons. Uses the shared derived data — no independent booking query.

import React, { useMemo } from 'react';
import { buildDailySchedule } from '../../../utils/bookingCRM';
import { staffStatusLabel } from '../../../utils/instructorCategory';
import { orgCategoryLabels, resolveOrgTimezone } from '../../../utils/organizationRuntime';
import { isClientBooking } from '../../../utils/scheduleModel';
import { ActivityBadge, PaymentBadge, fmtTime } from './opsShared';
import { zonedDateLabel, zonedTzAbbrev, LEGACY_OPS_TIMEZONE } from '../../../utils/opsTime';
import AssignControl from './AssignControl';

// Pass 58A — a staff time-off entry (RDO) is all-day; render it as a distinct gray "RDO · Day Off" chip
// (no guest / price / payment / coaching), never a lesson card. NOTE: this is time-off ONLY — other
// non-client activities (driving/concierge/admin) are NOT RDO and keep their normal entry.
const isTimeoff = (b) => b.activityType === 'timeoff' || b.scheduleEntryType === 'blocked';

export default function DayOps({ seasonBookings = [], seasonMembers = [], seasonId, organization = null, date, readOnly = false, conflicts = new Set(), onChanged, onOpenBooking }) {
  // Pass 82C.2 §18 — the org's timezone drives the day header + time labels; SAS resolves Asia/Tokyo (identical).
  const tz = resolveOrgTimezone(organization) || LEGACY_OPS_TIMEZONE;
  const rows = useMemo(
    // §4 pass seasonId so row category/rate/pool use the SELECTED season's affiliation.
    () => buildDailySchedule({ bookings: seasonBookings, members: seasonMembers, date, seasonId }),
    [seasonBookings, seasonMembers, date, seasonId],
  );
  const staffRows = rows.filter((r) => r.instructorId !== '__unassigned__');
  const unassignedRow = rows.find((r) => r.instructorId === '__unassigned__');

  const cell = { padding: '8px 10px', borderBottom: '1px solid #ECEAE6', verticalAlign: 'top' };

  const Entry = ({ b }) => {
    // RDO / staff time-off — distinct, all-day, no guest/payment. Not clickable into booking actions.
    if (isTimeoff(b)) {
      return (
        <span
          data-testid="day-rdo"
          title="Rostered day off — blocks teaching capacity"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 8px', margin: '2px 4px 2px 0', borderRadius: 8, background: '#F4F3F0', border: '1px dashed #C8C5BE', color: '#57534E' }}
        >
          <span style={{ fontSize: 11, fontFamily: "'DM Mono',monospace", color: '#9E9B95' }}>All day</span>
          <span style={{ fontSize: 10.5, fontWeight: 700 }}>RDO · Day Off</span>
        </span>
      );
    }
    const inner = (
      <>
        <span style={{ fontSize: 11, fontFamily: "'DM Mono',monospace", color: '#6B6860' }}>{fmtTime(b.startAt, tz)}{b.endAt ? `–${fmtTime(b.endAt, tz)}` : ''}</span>
        <ActivityBadge booking={b} />
        {b.requesterName || b.leadGuest?.fullName ? <span style={{ fontSize: 11, color: '#1A1917' }}>{b.requesterName || b.leadGuest?.fullName}</span> : null}
        <PaymentBadge booking={b} />
        {conflicts.has(b.id) ? <span style={{ fontSize: 10, color: '#DC2626', fontWeight: 700 }}>⚠ CONFLICT</span> : null}
      </>
    );
    const box = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 8px', margin: '2px 4px 2px 0', borderRadius: 8, background: '#FAF9F7', border: conflicts.has(b.id) ? '1px solid #DC2626' : '1px solid #ECEAE6' };
    // ONLY client bookings open the canonical Booking Detail (Publish Early / Cancel). Other operational
    // activities (driving/concierge/admin) render as before — no booking-detail actions apply to them.
    if (onOpenBooking && isClientBooking(b)) {
      return <button type="button" data-testid="day-entry" onClick={() => onOpenBooking(b)} style={{ ...box, cursor: 'pointer', font: 'inherit' }}>{inner}</button>;
    }
    return <span data-testid="day-entry" style={box}>{inner}</span>;
  };

  return (
    <div data-testid="day-ops">
      <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: "'DM Sans',sans-serif" }}>
        <thead>
          <tr style={{ textAlign: 'left', fontSize: 11, color: '#9E9B95', textTransform: 'uppercase', letterSpacing: 0.4 }}>
            <th style={{ ...cell, width: 210 }}>Staff / Instructor</th>
            <th style={cell}>Activities — {date ? `${zonedDateLabel(date, tz)} (${zonedTzAbbrev(tz, date)})` : ''}</th>
          </tr>
        </thead>
        <tbody>
          {staffRows.map((r) => {
            // §9/§10 use the source member (now on the row) + selected season; label keeps
            // commercial category and roster participation as separate, both-visible facts.
            const label = staffStatusLabel(r.member || {}, seasonId, orgCategoryLabels(organization));
            return (
              <tr key={r.instructorId} data-testid={`day-staff-${r.instructorId}`}>
                <td style={cell}>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{r.instructorName}</div>
                  <div style={{ fontSize: 10.5, color: '#9E9B95' }}>{label}</div>
                </td>
                <td style={cell}>
                  {r.bookings.length
                    ? r.bookings.map((b) => <Entry key={b.id} b={b} />)
                    : <span style={{ fontSize: 12, color: '#C8C5BE' }}>Available</span>}
                </td>
              </tr>
            );
          })}
          {!staffRows.length ? (
            <tr><td colSpan={2} style={{ ...cell, color: '#9E9B95', fontSize: 13 }}>No staff scheduled for this day.</td></tr>
          ) : null}
        </tbody>
      </table>

      {unassignedRow && unassignedRow.bookings.length ? (
        <div style={{ marginTop: 14, padding: 12, borderRadius: 10, background: '#FEF3C7' }} data-testid="day-unassigned">
          <div style={{ fontSize: 11, fontWeight: 700, color: '#92400E', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8 }}>Unassigned — {unassignedRow.bookings.length}</div>
          {unassignedRow.bookings.map((b) => (
            <div key={b.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 11, fontFamily: "'DM Mono',monospace", color: '#92400E' }}>{fmtTime(b.startAt, tz)}</span>
              <ActivityBadge booking={b} />
              {b.requesterName ? <span style={{ fontSize: 12 }}>{b.requesterName}</span> : null}
              {/* Pass 58G — the raw `src: <sourceInstructorLabel>` provenance is developer/audit detail; it
                  is NOT shown in the normal Day view (it remains visible in Booking Detail / Unassigned). */}
              <PaymentBadge booking={b} />
              <AssignControl booking={b} seasonMembers={seasonMembers} seasonId={seasonId} organization={organization} readOnly={readOnly} onAssigned={onChanged} />
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
